// Components
import { Col } from 'antd';
import { LibraryTitle } from '../Title';
import { ListItemComponent } from './ListCards';
import { CompactItemComponent } from './CompactCards';
import { LibraryFilters, SearchArea } from '../Filters';
import { highlightText } from './highlight';

// Redux
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import {
  getLibraryItems,
  normalizeLibraryFilter,
} from '../../../../../store/slices/yourLibrary';
import { GridItemComponent } from '../../../../Lists/list';
import { memo, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { isActiveOnOtherDevice } from '../../../../../store/slices/spotify';
import useIsMobile from '../../../../../utils/isMobile';
import { getLibraryCollapsed, getLibraryExpanded, uiActions } from '../../../../../store/slices/ui';
import { LanguageButton } from '../Language';
import { LibraryLoginInfo } from './loginInfo';
import type { LibraryWork } from '../../../../../interfaces/work';
import type { LibraryRecording } from '../../../../../interfaces/recording';
import type { LibraryComposer } from '../../../../../interfaces/composer';
import { ARTISTS_DEFAULT_IMAGE, PLAYLIST_DEFAULT_IMAGE } from '../../../../../constants/spotify';
import { getCatalogIdForSpotifyId } from '../../../../../utils/composerRegistry';
import { resolveWorkImageUrl } from './workUtils';
import { useComposerImageMap } from '../../../../../utils/useComposerImageMap';
import { selectOnboardingFlag } from '../../../../../store/slices/onboarding';

const COLLAPSED_STYLE = {
  height: '100%',
} as const;

const YourLibrary = () => {
  const collapsed = useAppSelector(getLibraryCollapsed);
  const expanded = useAppSelector(getLibraryExpanded);
  const user = useAppSelector((state) => !!state.auth.user);
  const activeOnOtherDevice = useAppSelector(isActiveOnOtherDevice);

  const heightValue = useMemo(() => {
    let value = 310;
    if (!user) value = 270;
    if (collapsed) value = 218;
    if (expanded) value = 230;
    if (activeOnOtherDevice) value += 50;
    return value;
  }, [user, collapsed, expanded, activeOnOtherDevice]);

  return (
    <div className={`Navigation-section library ${!collapsed ? 'open' : ''} ${expanded ? 'expanded' : ''}`}>
      <LibraryTitle />

      {!collapsed && user ? <LibraryFilters /> : null}

      <div className='library-list-container'>
        <Col style={collapsed ? {} : COLLAPSED_STYLE}>
          <div
            className='library-list'
            style={{
              maxHeight: `calc(100vh - ${heightValue}px)`,
            }}
          >
            {!user ? <AnonymousContent /> : <LoggedContent />}
          </div>

          {!user ? (
            <div style={{ marginLeft: 10 }}>
              <LanguageButton />
            </div>
          ) : null}
        </Col>
      </div>
    </div>
  );
};

const AnonymousContent = () => {
  return <LibraryLoginInfo />;
};

type LibraryItem = Parameters<typeof ListItemComponent>[0]['item'];
type NonWorkItem = Exclude<LibraryItem, LibraryWork | LibraryRecording | LibraryComposer>;

const ImportMusicCard = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const importCompletionFlag = useAppSelector((state) => selectOnboardingFlag(state, 'importCompletion'));
  const onboardingStatus = useAppSelector((state) => state.onboarding.status);

  const openSettings = () => {
    dispatch(uiActions.openImportOnboarding());
    dispatch(uiActions.markImportOnboardingSeen());
  };

  const flagSeen = Boolean(importCompletionFlag?.seenAt);
  const canDecide = flagSeen || importCompletionFlag !== null || onboardingStatus !== 'idle';
  if (!canDecide || flagSeen) {
    return null;
  }

  return (
    <div
      className='library-import-card'
      role='button'
      tabIndex={0}
      onClick={openSettings}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openSettings();
        }
      }}
    >
      <div className='library-import-card__left'>
        <div className='library-import-card__icon'>
          <svg
            viewBox='0 0 24 24'
            width='20'
            height='20'
            aria-hidden='true'
            focusable='false'
          >
            <path
              fill='currentColor'
              d='M12 3a1 1 0 0 1 1 1v8.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4.004 4.004a1 1 0 0 1-1.414 0L7.285 11.707a1 1 0 0 1 1.414-1.414L11 12.586V4a1 1 0 0 1 1-1Zm-7 12a1 1 0 0 0-1 1v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a1 1 0 1 0-2 0v3h-14v-3a1 1 0 0 0-1-1Z'
            />
          </svg>
        </div>
        <span className='library-import-card__text'>Import your classical music from Spotify</span>
      </div>
      <div className='library-import-card__chevron'>
        <svg
          viewBox='0 0 24 24'
          width='20'
          height='20'
          aria-hidden='true'
          focusable='false'
        >
          <path
            fill='currentColor'
            d='M8.293 4.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L13.586 12 8.293 6.707a1 1 0 0 1 0-1.414Z'
          />
        </svg>
      </div>
    </div>
  );
};

const LibraryWorkGridCard = memo(({ work }: { work: LibraryWork }) => {
  const navigate = useNavigate();
  const search = useAppSelector((state) => state.yourLibrary.search);
  const composerImages = useComposerImageMap();
  const catalogueDisplay = work.catalogueNumberNormalized ?? work.catalogueNumber;
  const movementLabel =
    typeof work.movementCount === 'number'
      ? `${work.movementCount} movement${work.movementCount === 1 ? '' : 's'}`
      : undefined;
  const descriptionParts = [work.composerName, catalogueDisplay, movementLabel].filter(Boolean);

  const description = descriptionParts.length ? descriptionParts.join(' • ') : 'Work';
  const artwork = resolveWorkImageUrl(work, composerImages) ?? PLAYLIST_DEFAULT_IMAGE;

  return (
    <div
      className='playlist-card relative rounded-lg overflow-hidden  hover:bg-spotify-gray-lightest transition'
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/work/${work.id}`)}
    >
      <div
        style={{ position: 'relative' }}
        className='aspect-square md:aspect-w-1 md:aspect-h-1/2 lg:aspect-w-1 lg:aspect-h-3/4 xl:aspect-w-1 xl:aspect-h-4/5 p-4'
      >
        <img
          src={artwork}
          alt={work.name}
          style={{ borderRadius: 5, width: '100%' }}
        />
      </div>
      <div className='playlist-card-info'>
        <h3 className='text-md font-semibold text-white'>
          {highlightText(work.name ?? '', search)}
        </h3>
        <p>{highlightText(description, search)}</p>
      </div>
    </div>
  );
});

const LibraryRecordingGridCard = memo(({ recording }: { recording: LibraryRecording }) => {
  const navigate = useNavigate();
  const search = useAppSelector((state) => state.yourLibrary.search);

  const descriptionParts = [
    recording.performerName,
    recording.albumName,
    recording.year,
    recording.durationLabel,
  ].filter(Boolean);

  const description = descriptionParts.length ? descriptionParts.join(' • ') : 'Recording';

  return (
    <div
      className='playlist-card relative rounded-lg overflow-hidden  hover:bg-spotify-gray-lightest transition'
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/recording/${recording.id}`)}
    >
      <div
        style={{ position: 'relative' }}
        className='aspect-square md:aspect-w-1 md:aspect-h-1/2 lg:aspect-w-1 lg:aspect-h-3/4 xl:aspect-w-1 xl:aspect-h-4/5 p-4'
      >
        <img
          src={recording.imageUrl ?? recording.albumImageUrl ?? PLAYLIST_DEFAULT_IMAGE}
          alt={recording.workName}
          style={{ borderRadius: 5, width: '100%' }}
        />
      </div>
      <div className='playlist-card-info'>
        <h3 className='text-md font-semibold text-white'>
          {highlightText(recording.workName ?? '', search)}
        </h3>
        <p>{highlightText(description, search)}</p>
      </div>
    </div>
  );
});

const LibraryComposerGridCard = memo(({ composer }: { composer: LibraryComposer }) => {
  const navigate = useNavigate();
  const search = useAppSelector((state) => state.yourLibrary.search);
  const fallbackImage = useAppSelector((state) => {
    const targetIds = [composer.spotifyArtistId, composer.id].filter(Boolean);
    const match = state.yourLibrary.myArtists.find((entry) => targetIds.includes(entry.id));
    return match?.images?.[0]?.url;
  });

  const lifespan = [composer.birthYear, composer.deathYear].filter(Boolean).join('–');
  const descriptionParts = [composer.era, lifespan].filter(Boolean);
  const description = descriptionParts.length ? descriptionParts.join(' • ') : 'Composer';

  return (
    <div
      className='playlist-card relative rounded-lg overflow-hidden  hover:bg-spotify-gray-lightest transition'
      style={{ cursor: 'pointer' }}
      onClick={() => {
        const targetId =
          composer.id ??
          getCatalogIdForSpotifyId(composer.spotifyArtistId ?? composer.id ?? null) ??
          composer.spotifyArtistId ??
          composer.id;
        navigate(`/composer/${targetId}`);
      }}
    >
      <div
        style={{ position: 'relative' }}
        className='aspect-square md:aspect-w-1 md:aspect-h-1/2 lg:aspect-w-1 lg:aspect-h-3/4 xl:aspect-w-1 xl:aspect-h-4/5 p-4'
      >
        <img
          src={composer.imageUrl ?? fallbackImage ?? ARTISTS_DEFAULT_IMAGE}
          alt={composer.name}
          style={{ borderRadius: '50%', width: '100%', objectFit: 'cover' }}
        />
      </div>
      <div className='playlist-card-info'>
        <h3 className='text-md font-semibold text-white'>
          {highlightText(composer.name ?? '', search)}
        </h3>
        <p>{highlightText(description, search)}</p>
      </div>
    </div>
  );
});

const renderGridItem = (item: LibraryItem) => {
  if (item.type === 'work') {
    return <LibraryWorkGridCard work={item} />;
  }
  if (item.type === 'recording') {
    return <LibraryRecordingGridCard recording={item} />;
  }
  if (item.type === 'composer') {
    return <LibraryComposerGridCard composer={item} />;
  }
  return <GridItemComponent item={item as NonWorkItem} />;
};

const LoggedContent = memo(() => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const items = useAppSelector(getLibraryItems);
  const collapsed = useAppSelector(getLibraryCollapsed);
  const view = useAppSelector((state) => state.yourLibrary.view);
  const filter = useAppSelector((state) => state.yourLibrary.filter);
  const search = useAppSelector((state) => state.yourLibrary.search);
  const recordingsStatus = useAppSelector((state) => state.yourLibrary.recordingsStatus);
  const playlistsStatus = useAppSelector((state) => state.yourLibrary.playlistsStatus);
  const albumsStatus = useAppSelector((state) => state.yourLibrary.albumsStatus);
  const artistsStatus = useAppSelector((state) => state.yourLibrary.artistsStatus);

  const normalizedFilter = useMemo(() => normalizeLibraryFilter(filter), [filter]);
  const normalizedSearch = useMemo(() => search.trim().toLowerCase(), [search]);

  const filteredItems = useMemo(() => {
    const matchFilter = (item: LibraryItem) => {
      if (normalizedFilter === 'ALL') return true;
      if (normalizedFilter === 'ALBUMS') return item.type === 'album';
      if (normalizedFilter === 'ARTISTS') return item.type === 'artist';
      if (normalizedFilter === 'PLAYLISTS') return item.type === 'playlist';
      if (normalizedFilter === 'COMPOSERS') return item.type === 'composer';
      if (normalizedFilter === 'WORKS') return item.type === 'work';
      if (normalizedFilter === 'RECORDINGS') return item.type === 'recording';
      return true;
    };
    const matchSearch = (item: LibraryItem) => {
      if (!normalizedSearch.length) return true;

      const terms: string[] = [];
      const addTerm = (value?: unknown) => {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed) {
            terms.push(trimmed.toLowerCase());
          }
        } else if (typeof value === 'number' && Number.isFinite(value)) {
          terms.push(String(value));
        }
      };

      if ('name' in item && typeof item.name === 'string') {
        addTerm(item.name);
      }

      if (item.type === 'playlist') {
        addTerm(item.owner?.display_name);
        addTerm(item.description);
      } else if (item.type === 'album') {
        item.artists?.forEach((artist) => addTerm(artist?.name));
      } else if (item.type === 'artist') {
        item.genres?.forEach(addTerm);
      } else if (item.type === 'composer') {
        addTerm(item.era);
        addTerm(item.birthYear);
        addTerm(item.deathYear);
      } else if (item.type === 'work') {
        addTerm(item.composerName);
        addTerm(item.catalogueNumber);
        addTerm(item.catalogueNumberNormalized);
      } else if (item.type === 'recording') {
        addTerm(item.workName);
        addTerm(item.performerName);
        addTerm(item.albumName);
        addTerm(item.year);
        if (Array.isArray(item.labels)) {
          item.labels.forEach(addTerm);
        }
      }

      return terms.some((term) => term.includes(normalizedSearch));
    };
    return items.filter((item) => matchFilter(item) && matchSearch(item));
  }, [items, normalizedFilter, normalizedSearch]);

  const showWorkEmptyState = normalizedFilter === 'WORKS' && filteredItems.length === 0;
  const showRecordingEmptyState =
    normalizedFilter === 'RECORDINGS' &&
    recordingsStatus === 'succeeded' &&
    filteredItems.length === 0;
  const showRecordingLoadingState =
    normalizedFilter === 'RECORDINGS' &&
    recordingsStatus === 'loading' &&
    filteredItems.length === 0;
  const showRecordingFailedState =
    normalizedFilter === 'RECORDINGS' && recordingsStatus === 'failed';
  const showPlaylistEmptyState =
    normalizedFilter === 'PLAYLISTS' &&
    filteredItems.length === 0 &&
    playlistsStatus !== 'loading';
  const showAlbumEmptyState =
    normalizedFilter === 'ALBUMS' && filteredItems.length === 0 && albumsStatus !== 'loading';
  const showArtistEmptyState =
    normalizedFilter === 'ARTISTS' && filteredItems.length === 0 && artistsStatus !== 'loading';
  const showComposerEmptyState = normalizedFilter === 'COMPOSERS' && filteredItems.length === 0;

  const messageStyle = {
    padding: '16px',
    color: 'var(--text-subdued, #b3b3b3)',
    fontSize: 13,
  } as const;

  let content: ReactNode;

  if (showRecordingLoadingState) {
    content = <div style={messageStyle}>Loading your saved recordings…</div>;
  } else if (showRecordingFailedState) {
    content = (
      <div style={messageStyle}>We could not load your favorite recordings. Please try again.</div>
    );
  } else if (showRecordingEmptyState) {
    content = (
      <div style={messageStyle}>No favorite recordings yet. Save a recording to see it here.</div>
    );
  } else if (showPlaylistEmptyState) {
    content = (
      <div style={messageStyle}>
        You don't have any saved playlists yet. Once you save or follow a playlist, it will appear
        here.
      </div>
    );
  } else if (showAlbumEmptyState) {
    content = (
      <div style={messageStyle}>
        You don't have any saved albums yet. Once you save an album, it will appear here.
      </div>
    );
  } else if (showArtistEmptyState) {
    content = (
      <div style={messageStyle}>
        You don't follow any artists yet. Once you follow an artist, they will appear here.
      </div>
    );
  } else if (showComposerEmptyState) {
    content = (
      <div style={messageStyle}>
        You don't follow any composers yet. Once you follow a composer, they will appear here.
      </div>
    );
  } else if (showWorkEmptyState) {
    content = (
      <div style={messageStyle}>No favorite works yet. Save a work to see it here.</div>
    );
  } else {
    content = filteredItems.map((item) => {
      const itemKey = `${item.type}-${item.id}`;
      if (collapsed) return <ListItemComponent key={itemKey} item={item} />;

      return (
        <div
          key={itemKey}
          onClick={isMobile ? () => dispatch(uiActions.collapseLibrary()) : undefined}
        >
          {view === 'LIST' ? <ListItemComponent item={item} /> : null}
          {view === 'COMPACT' ? <CompactItemComponent item={item} /> : null}
          {view === 'GRID' ? renderGridItem(item) : null}
        </div>
      );
    });
  }

  const showImportCard = !collapsed && normalizedFilter === 'ALL';

  return (
    <>
      {showImportCard ? <ImportMusicCard /> : null}
      {!collapsed ? <SearchArea /> : null}

      <div
        className={`${collapsed ? 'collapsed' : ''} ${
          !collapsed && view === 'GRID' ? 'grid-view' : ''
        }`}
      >
        {content}
      </div>
    </>
  );
});

export default YourLibrary;
